import { Fragment, ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import MidLockup from '../../components/MidLockup';
import LandingFooter from '../landing/LandingFooter';
import ZoningPlan from '../landing/ZoningPlan';
import { applyLandingMetadata } from '../landing/landingSeo';
import NotFoundPage from '../NotFoundPage';
import BuyingCostsCalculator from './BuyingCostsCalculator';
import { GuideIllustration } from './GuideIllustrations';
import { findGuide, formatReviewedOn, Guide, GuideCallout, GuideInline, GuideParagraph, GuideSection } from './guidesContent';
import '../landing/landing.css';

const CHECKLIST_STORAGE_KEY = 'mid_guide_checklist';

const readStoredChecks = (): string[] => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(CHECKLIST_STORAGE_KEY) || '[]');
    return Array.isArray(stored) ? stored.filter((item) => typeof item === 'string') : [];
  } catch {
    return []; // No window in the prerender; storage can also be blocked.
  }
};

const writeStoredChecks = (checked: string[]) => {
  try {
    if (checked.length) window.localStorage.setItem(CHECKLIST_STORAGE_KEY, JSON.stringify(checked));
    else window.localStorage.removeItem(CHECKLIST_STORAGE_KEY);
  } catch {
    // Storage blocked: the list still works until the page closes.
  }
};

/** Checked items survive a reload, because the list is used mid-visit on a phone. Nothing leaves the device. */
function useChecklist(items: string[]) {
  const [checked, setChecked] = useState<string[]>(readStoredChecks);
  useEffect(() => writeStoredChecks(checked), [checked]);
  const toggle = (item: string) => setChecked((current) => (
    current.includes(item) ? current.filter((entry) => entry !== item) : [...current, item]
  ));
  return {
    isChecked: (item: string) => checked.includes(item),
    toggle,
    reset: () => setChecked([]),
    doneCount: items.filter((item) => checked.includes(item)).length,
  };
}

const isExternal = (href: string) => href.startsWith('http');

function InlineText({ part }: { part: GuideInline }) {
  if (typeof part === 'string') return <>{part}</>;
  if (isExternal(part.href)) return <a href={part.href} rel="noopener noreferrer" target="_blank">{part.text}</a>;
  return <Link to={part.href}>{part.text}</Link>;
}

function Paragraph({ parts }: { parts: GuideParagraph }) {
  return <p>{parts.map((part, index) => <InlineText key={index} part={part} />)}</p>;
}

/** Headings render uppercase, but the name stays "miD" (The Name Keeps Its Case Rule). */
function BrandSafeText({ text }: { text: string }) {
  return <>{text.split(/(miD)/).map((part, index) => (part === 'miD' ? <span className="guide-name" key={index}>miD</span> : part))}</>;
}

function SectionHeading({ heading }: { heading: string }) {
  const numbered = heading.match(/^(\d+)\.\s+(.*)$/);
  if (!numbered) return <h2><BrandSafeText text={heading} /></h2>;
  return <h2 className="guide-numbered"><span className="guide-number">{numbered[1]}</span><span><BrandSafeText text={numbered[2]} /></span></h2>;
}

function GuideTableBlock({ table }: { table: NonNullable<GuideSection['table']> }) {
  return (
    <div className="guide-table-scroll">
      <table className="guide-table">
        <caption>{table.caption}</caption>
        <thead><tr>{table.headers.map((header) => <th key={header} scope="col">{header}</th>)}</tr></thead>
        <tbody>{table.rows.map((row) => <tr key={row[0]}>{row.map((cell, index) => <td key={index}>{cell}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

function CalloutBand({ callout }: { callout: GuideCallout }) {
  return (
    <section className="guide-callout" aria-label="Em destaque">
      <div className="guide-wrap">
        {callout.statement && <p className="guide-callout-statement">{callout.statement}</p>}
        {callout.figures && (
          <dl className="guide-callout-figures">
            {callout.figures.map((figure) => (
              <div key={figure.value}><dt>{figure.value}</dt><dd>{figure.label}</dd></div>
            ))}
          </dl>
        )}
      </div>
    </section>
  );
}

function ChecklistProgress({ done, total, onReset }: { done: number; total: number; onReset: () => void }) {
  return (
    <section className="guide-progress" aria-label="Progresso da checklist">
      <div className="guide-wrap guide-progress-inner">
        <p aria-live="polite"><strong>{done}</strong> de <strong>{total}</strong> pontos vistos</p>
        <div className="guide-progress-squares" aria-hidden="true">
          {Array.from({ length: total }, (_, index) => <i key={index} className={index < done ? 'is-done' : undefined} />)}
        </div>
        <button type="button" onClick={onReset} disabled={done === 0}>Recomeçar</button>
      </div>
    </section>
  );
}

function SectionBand({ section, index, children }: { section: GuideSection; index: number; children?: ReactNode }) {
  const hasAside = Boolean(section.illustration || section.table);
  const isChecklist = Boolean(section.checklist);
  const classes = [
    'guide-band',
    index % 2 === 1 && 'guide-band-alt',
    hasAside && 'guide-band-split',
    isChecklist && 'guide-band-checklist',
    index % 2 === 1 && hasAside && !isChecklist && 'guide-band-flip',
  ].filter(Boolean).join(' ');
  return (
    <section className={classes} id={section.id}>
      <div className="guide-wrap guide-band-grid">
        <div className="guide-band-copy">
          <SectionHeading heading={section.heading} />
          {section.paragraphs?.map((parts, paragraphIndex) => <Paragraph key={paragraphIndex} parts={parts} />)}
          {isChecklist && section.illustration && <div className="guide-figure guide-figure-small"><GuideIllustration id={section.illustration} /></div>}
        </div>
        {isChecklist ? <div className="guide-band-list">{children}</div> : (
          hasAside && (
            <div className="guide-figure">
              {section.illustration && <GuideIllustration id={section.illustration} />}
              {section.table && <GuideTableBlock table={section.table} />}
            </div>
          )
        )}
      </div>
    </section>
  );
}

function GuideIndex({ guide }: { guide: Guide }) {
  const entries = guide.sections.filter((section) => section.shortLabel && section.id);
  if (!entries.length) return null;
  const fills = ['hab', 'verde', 'equip', 'hab', 'ind', 'verde', 'equip'];
  return (
    <nav className="guide-index" aria-label="Neste guia">
      <ol className="guide-wrap">
        {entries.map((section, index) => (
          <li key={section.id}>
            <a href={`#${section.id}`}>
              <span className={`zp-swatch zp-swatch-${fills[index % fills.length]}`} aria-hidden="true" />
              {section.shortLabel}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

export default function GuidePage() {
  const { pathname } = useLocation();
  const guide = findGuide(pathname);
  const checklistItems = useMemo(() => (guide?.sections ?? []).flatMap((section) => section.checklist ?? []), [guide]);
  const { isChecked, toggle, reset, doneCount } = useChecklist(checklistItems);

  useEffect(() => {
    if (guide) return applyLandingMetadata(guide.path);
    return undefined;
  }, [guide]);

  // ponytail: an unknown /guias/x renders the not-found page with status 200; the app shell
  // stays noindex, so it is not indexed. Send it to spa.py's 404 if guides outgrow a short list.
  if (!guide) return <NotFoundPage />;

  return (
    <div className="landing-home guide-page" data-testid="guide-page">
      <header className="landing-header">
        <div className="guide-wrap landing-header-inner">
          <Link to="/" aria-label="miD, página inicial"><MidLockup variant="neutro" fontSize={36} animated={false} color="var(--brand-ink, #FAFAF7)" tone="auto" /></Link>
          <Link to="/login">Entrar</Link>
        </div>
      </header>
      <main>
        <article>
          <section className="guide-hero">
            <div className="guide-wrap guide-hero-grid">
              <div className="guide-hero-copy">
                <p className="landing-eyebrow"><span aria-hidden="true" className="landing-eyebrow-mark"><i /><i /><i /></span>Guia para quem compra casa</p>
                <h1>{guide.h1}</h1>
                <p className="landing-hero-subtitle">{guide.lede}</p>
                <p className="guide-reviewed">Última revisão: <time dateTime={guide.reviewedOn}>{formatReviewedOn(guide.reviewedOn)}</time>. Escrito pelo <span className="guide-name">miD</span>.</p>
              </div>
              <div className="guide-hero-art">
                {guide.heroIllustration ? <GuideIllustration id={guide.heroIllustration} /> : <ZoningPlan />}
              </div>
            </div>
          </section>
          <GuideIndex guide={guide} />
          {checklistItems.length > 0 && <ChecklistProgress done={doneCount} total={checklistItems.length} onReset={reset} />}
          {guide.hasCalculator && (
            <section className="guide-band guide-band-calculator">
              <div className="guide-wrap"><BuyingCostsCalculator /></div>
            </section>
          )}
          {guide.sections.map((section, index) => (
            <Fragment key={section.heading}>
              <SectionBand section={section} index={index}>
                {section.checklist && (
                  <ul className="guide-checklist">
                    {section.checklist.map((item) => (
                      <li key={item} className={isChecked(item) ? 'is-done' : undefined}>
                        <label>
                          <input type="checkbox" checked={isChecked(item)} onChange={() => toggle(item)} />
                          <span>{item}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </SectionBand>
              {section.callout && <CalloutBand callout={section.callout} />}
            </Fragment>
          ))}
        </article>
        <section className="guide-band guide-band-alt landing-faq">
          <div className="guide-wrap guide-faq-grid">
            <h2>Perguntas frequentes</h2>
            <div className="landing-faq-items">
              {guide.faq.map(({ question, answer }) => (
                <details key={question}>
                  <summary>{question}</summary>
                  <p>{answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
        <section className="guide-cta">
          <div className="guide-wrap guide-cta-grid">
            <div>
              <h2>{guide.ctaTitle}</h2>
              <p>{guide.ctaBody}</p>
            </div>
            <Link className="landing-button landing-button-primary" to="/comecar?from=comprar">Quero comprar casa</Link>
          </div>
        </section>
        <section className="guide-sources">
          <div className="guide-wrap guide-faq-grid">
            <h2>Fontes</h2>
            <div>
              <ul>
                {guide.sources.map((source) => (
                  <li key={source.href}><a href={source.href} rel="noopener noreferrer" target="_blank">{source.text}</a></li>
                ))}
              </ul>
              <p>Estes conteúdos ajudam-te a preparar a decisão. Não substituem aconselhamento jurídico, fiscal ou financeiro.</p>
            </div>
          </div>
        </section>
      </main>
      <LandingFooter />
    </div>
  );
}
